import { useTour } from '@reactour/tour';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import findIndex from 'lodash.findindex';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Scatter } from 'react-chartjs-2';

import { getTourStep } from '@/features/demo/steps';
import { AnalysisPage } from '@/features/single-measurement';
import createBatch from '@/features/single-measurement/dto/batch';
import { MeasurementOverviewLayout } from '@/layouts';
import { isResultReady } from '@/utils/is-result-ready';

import createFermentation from '../../dto/fermentation';
import { getMockBatch, tourBatchesMock, tourFermentationMock } from '../../tour-mock';
import { fermentationGraphDataMapper, fermentationGraphOptions } from '../../utils/graph';
import { BatchTabGroup } from './components/batch-tab-group';
import { GraphAnalysisList } from './components/graph-analysis-list';
import { TagList } from './components/tag-list';
import { FermentationTopBar } from './components/top-bar';
import { WarningsList } from './components/warnings-list';

ChartJS.register(
  Title,
  Legend,
  Tooltip,
  LineElement,
  LinearScale,
  PointElement,
  CategoryScale,
);

export const FermentationTourPage = () => {
  const chartRef = useRef<any>(null);

  // demo
  const { setIsOpen: setIsTourOpen, setCurrentStep, steps } = useTour();
  const [mockBatches, setMockBatches] = useState(tourBatchesMock);

  useEffect(() => {
    setIsTourOpen(true);
    setCurrentStep(getTourStep(steps, 'prahZDR'));
  }, []);

  // get fermentation data
  const data = useMemo(() => createFermentation(tourFermentationMock), []);

  // result ready batches
  const batches = useMemo(
    () =>
      mockBatches
        .map((d) => createBatch(d))
        ?.filter((batch) => isResultReady(batch.state))
        .reverse(),
    [mockBatches],
  );

  // graph data set
  const datasets = useMemo(
    () => fermentationGraphDataMapper({ batches, method: data?.method }),
    [batches, data?.method],
  );

  return (
    <MeasurementOverviewLayout>
      <FermentationTopBar />
      <MeasurementOverviewLayout.Title title={data?.name ?? ''} />

      <MeasurementOverviewLayout.Body>
        <div className="flex flex-col w-full mt-10 -mb-40 gap-10">
          <div id="step-fermentation-graph-list">
            <GraphAnalysisList
              batches={batches}
              onListUpdate={() => {}}
              onDemoListUpdate={(id, state) => {
                const batchIndex = findIndex(mockBatches, { id });
                setMockBatches((d) => {
                  d[batchIndex]['state'] = state!;
                  return [...d];
                });
              }}
            />
          </div>

          <WarningsList batches={batches} />

          <div id="step-fermentation-graph">
            <Scatter options={fermentationGraphOptions} data={datasets} ref={chartRef} />
          </div>

          <div id="step-fermentation-details">
            <TagList tags={data?.tags} />
          </div>

          <div id="step-fermentation-tabs">
            <BatchTabGroup batches={batches} isLoading={false} fermentationId={'tour'} />
          </div>
        </div>
      </MeasurementOverviewLayout.Body>

      <div id="step-fermentation-single-analysis">
        <AnalysisPage batchApiHandler={getMockBatch} />
      </div>
    </MeasurementOverviewLayout>
  );
};
